<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import BusinessAPI from 'dashboard/api/businessConnectors';
import Button from 'dashboard/components-next/button/Button.vue';
import BusinessProfileFields from 'dashboard/components/widgets/conversation/BusinessProfileFields.vue';
const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  contactId: { type: [Number, String], default: null },
});
const { t } = useI18n();
const records = ref([]);
const connectorId = ref(null);
const customerId = ref('');
const profile = ref(null);
const loading = ref(false);
const busy = ref(false);
const error = ref('');
const role = useMapGetter('getCurrentRole');
const getContact = useMapGetter('contacts/getContact');
const selected = computed(() =>
  records.value.find(record => record.id === connectorId.value)
);
const automaticCustomerId = computed(
  () =>
    selected.value?.identity_customer_id ||
    selected.value?.suggested_customer_id
);
const contactSourceValue = computed(() => {
  const source = selected.value?.customer_id_source;
  if (!source) return undefined;
  const contact = getContact.value(props.contactId);
  if (source === 'name') return contact.email || contact.name;
  return source.startsWith('custom_attributes.')
    ? contact.custom_attributes?.[source.slice('custom_attributes.'.length)]
    : contact[source];
});
// The ID is pre-filled from the contact, but staff may always override it.
const isAutoFilled = computed(
  () =>
    Boolean(automaticCustomerId.value) &&
    customerId.value === automaticCustomerId.value
);
let listSequence = 0;
let querySequence = 0;
const errorText = err =>
  t(`BUSINESS.ERRORS.${err.response?.data?.code || 'connection_failed'}`);
watch(
  customerId,
  () => {
    querySequence += 1;
    profile.value = null;
    error.value = '';
    busy.value = false;
  },
  { flush: 'sync' }
);
const query = async (link = false) => {
  querySequence += 1;
  const sequence = querySequence;
  const conversation = props.conversationId;
  const connector = connectorId.value;
  busy.value = true;
  error.value = '';
  profile.value = null;
  try {
    const body = { connector_id: connector };
    if (customerId.value) body.customer_id = customerId.value;
    const { data } = await (link
      ? BusinessAPI.bind(conversation, body)
      : BusinessAPI.lookup(conversation, body));
    if (
      sequence !== querySequence ||
      conversation !== props.conversationId ||
      connector !== connectorId.value
    )
      return;
    profile.value = data;
    if (data.linked)
      selected.value.link = {
        external_customer_id: data.customer_id,
        source: data.source,
      };
  } catch (err) {
    if (sequence === querySequence) error.value = errorText(err);
  } finally {
    if (sequence === querySequence) busy.value = false;
  }
};
watch(
  connectorId,
  () => {
    querySequence += 1;
    profile.value = null;
    error.value = '';
    busy.value = false;
    customerId.value =
      automaticCustomerId.value ||
      selected.value?.link?.external_customer_id ||
      '';
    if (customerId.value) query();
  },
  { flush: 'sync' }
);
const load = async () => {
  const previousConnectorId = connectorId.value;
  listSequence += 1;
  const sequence = listSequence;
  querySequence += 1;
  records.value = [];
  connectorId.value = null;
  profile.value = null;
  error.value = '';
  loading.value = true;
  try {
    const { data } = await BusinessAPI.profiles(props.conversationId);
    if (sequence !== listSequence) return;
    records.value = data;
    connectorId.value = data.some(item => item.id === previousConnectorId)
      ? previousConnectorId
      : data[0]?.id || null;
  } catch (err) {
    if (sequence === listSequence) error.value = errorText(err);
  } finally {
    if (sequence === listSequence) loading.value = false;
  }
};
const unlink = async () => {
  querySequence += 1;
  const sequence = querySequence;
  const conversation = props.conversationId;
  const connector = connectorId.value;
  busy.value = true;
  try {
    await BusinessAPI.unbind(conversation, connector);
    if (
      sequence !== querySequence ||
      conversation !== props.conversationId ||
      connector !== connectorId.value
    )
      return;
    selected.value.link = null;
    profile.value = null;
    customerId.value = automaticCustomerId.value || '';
    error.value = '';
  } catch (err) {
    if (sequence === querySequence) error.value = errorText(err);
  } finally {
    if (sequence === querySequence) busy.value = false;
  }
};
watch(() => [props.conversationId, props.contactId], load, { immediate: true });
watch(
  [() => selected.value?.customer_id_source, contactSourceValue],
  ([source, value], [previousSource, previousValue]) => {
    if (source && source === previousSource && value !== previousValue) load();
  }
);
</script>

<template>
  <section
    class="mx-2 my-3 p-3 rounded-xl border border-n-weak bg-n-background grid gap-3"
  >
    <h3 class="text-sm font-medium text-n-slate-12">
      {{ t('BUSINESS.CARD_TITLE') }}
    </h3>
    <p class="text-xs text-n-slate-11">{{ t('BUSINESS.STAFF_ONLY') }}</p>
    <p v-if="loading" class="text-sm text-n-slate-11">
      {{ t('BUSINESS.LOADING') }}
    </p>
    <p v-else-if="!records.length && !error" class="text-sm text-n-slate-11">
      {{ t('BUSINESS.NO_ENABLED') }}
    </p>
    <template v-if="records.length">
      <label class="text-sm text-n-slate-12 flex flex-col gap-1"
        >{{ t('BUSINESS.SYSTEM')
        }}<select v-model.number="connectorId">
          <option v-for="record in records" :key="record.id" :value="record.id">
            {{ record.name }}
          </option>
        </select></label
      >
      <p v-if="selected?.identity_customer_id" class="text-xs text-n-slate-11">
        {{ t('BUSINESS.SIGNED_IDENTITY') }} ·
        {{ selected.identity_customer_id }}
      </p>
      <p v-else class="text-xs text-n-slate-11">
        {{ t('BUSINESS.IDENTITY_UNVERIFIED') }}
      </p>
      <p
        v-if="
          selected?.suggested_customer_id && !selected?.identity_customer_id
        "
        class="text-xs text-n-slate-11"
      >
        {{
          t('BUSINESS.CUSTOMER_ID_AUTO_FILLED', {
            source: selected.customer_id_source,
          })
        }}
      </p>
      <p
        v-else-if="selected?.customer_id_source && !automaticCustomerId"
        class="text-xs text-n-amber-11"
      >
        {{
          t('BUSINESS.CUSTOMER_ID_SOURCE_EMPTY', {
            source: selected.customer_id_source,
          })
        }}
      </p>
      <form class="grid gap-2" @submit.prevent="query(false)">
        <label class="text-sm text-n-slate-12 flex flex-col gap-1"
          >{{ t('BUSINESS.CUSTOMER_ID')
          }}<input
            v-model="customerId"
            type="text"
            required
            maxlength="200"
            :disabled="busy"
        /></label>
        <p v-if="automaticCustomerId" class="text-xs text-n-slate-11">
          {{ t('BUSINESS.CUSTOMER_ID_EDITABLE_HINT') }}
        </p>
        <Button
          type="submit"
          :label="
            t(isAutoFilled ? 'BUSINESS.REFRESH_PROFILE' : 'BUSINESS.LOOKUP')
          "
          :disabled="busy"
          :is-loading="busy"
        />
      </form>
      <template v-if="profile">
        <p role="status" class="text-xs text-n-slate-11">
          {{ t(profile.linked ? 'BUSINESS.LINKED' : 'BUSINESS.PREVIEW_ONLY') }}
          · {{ profile.customer_id }}
        </p>
        <BusinessProfileFields :fields="profile.fields" />
        <Button
          v-if="!profile.linked"
          :label="t('BUSINESS.LINK_CUSTOMER')"
          :disabled="busy"
          @click="query(true)"
        />
      </template>
      <Button
        v-if="selected?.link"
        :label="t('BUSINESS.UNLINK')"
        variant="ghost"
        color="slate"
        :disabled="busy"
        @click="unlink"
      />
    </template>
    <p v-if="error" role="alert" class="text-sm text-n-ruby-11">{{ error }}</p>
    <Button
      v-if="error && !records.length"
      :label="t('BUSINESS.RETRY')"
      color="slate"
      @click="load"
    />
    <RouterLink
      v-if="role === 'administrator'"
      :to="{ name: 'business_connectors_settings' }"
      class="text-xs text-n-brand"
    >
      {{ t('BUSINESS.MANAGE') }}
    </RouterLink>
  </section>
</template>
